import { useMutation } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { defineMessages, useIntl } from "react-intl";

import { useApi } from "@/hooks";
import toast from "@/toast";
import { type ApiError } from "@/types/api";

import { updateStatusCache } from "../utils";

const messages = defineMessages({
  translationFailed: {
    id: "status.translation.error",
    defaultMessage: "Failed to translate status.",
  },
});

interface Variables {
  id: string;
  text: string;
  title?: string;
  from_lang: string;
  to_lang: string;
}

interface TranslationResponse {
  id: string;
  from_lang: string;
  to_lang: string;
  text: string;
  title: string;
}

const API_URL = "/api/v1/truth/ai/status/translation";

function useTranslateStatus() {
  const intl = useIntl();
  const api = useApi();

  return useMutation<
    { textData: TranslationResponse; titleData?: TranslationResponse },
    AxiosError<ApiError>,
    Variables
  >({
    async mutationFn(variables) {
      const { id, text, title, from_lang, to_lang } = variables;

      const requests = [
        api.post(API_URL, {
          id,
          text,
          from_lang,
          to_lang,
        }),
      ];

      // Second request for title, if available
      if (title) {
        requests.push(
          api.post(API_URL, {
            id: `${id}-title`,
            text: title,
            from_lang,
            to_lang,
          }),
        );
      }

      const [textRes, titleRes] = await Promise.all(requests);

      return {
        textData: textRes.data as TranslationResponse,
        titleData: titleRes?.data as TranslationResponse | undefined,
      };
    },
    onSuccess(response, variables) {
      const { id, from_lang } = variables;
      const { textData, titleData } = response;

      updateStatusCache(id, () => ({
        translatedContent: textData.text,
        translatedTitle: titleData?.text || "",
        translatedFrom: from_lang,
      }));
    },

    onError() {
      toast.error(intl.formatMessage(messages.translationFailed));
    },
  });
}

export { useTranslateStatus };
